import React, { FC, useState } from "react";
// libs
import NextImage from "next/image";
import Slider from "react-slick";
// components
import Button from "src/components/ui/button/button";
// hooks
import { useAppSelector, useWindowSize } from "src/hooks";
import { useRouter } from "next/router";
// utils
import { getBoundsByCenter } from "src/utils/map";
// redux
import { authUserGeometrySelector, authUserLocationSelector } from "src/redux/slices";
// const
import { EVariants } from "src/constants/variants";
import { sizesMedia } from "src/constants/media";
import { zoom } from "src/constants/map";
// theme
// types
import { IService } from "src/types";
import { IGeometry, LngLatBounds } from "src/constants/coordinates";
import {
  Wrapper,
  CardsWrapper,
  ButtonWrapper,
  Content,
  SliderCardWrapper,
  ImageContainer,
  Waves,
} from "./service.styles";
import { generateSliderSettings, generateSliderSettings2 } from "./sliderSettings";
import { amplitudeEvent, formatEventName } from "src/libs/amplitude";
import { Headline, LargeTitle } from "src/theme/typography";
import { colors } from "src/theme";

export interface IServiceSection {
  services: IService[];
}

const ServiceSection: FC<IServiceSection> = ({ services }) => {
  const router = useRouter();
  const size = useWindowSize();
  const userLocation: string = useAppSelector(authUserLocationSelector);
  const { center }: IGeometry = useAppSelector(authUserGeometrySelector);

  const settings = generateSliderSettings(size);
  const settings2 = generateSliderSettings2(size);

  const [open, setOpen] = useState(false);

  const handleService = () => setOpen(!open);

  const redirectByService = (service: IService) => () => {
    const bounds = userLocation
      ? (getBoundsByCenter(center, zoom.service) as LngLatBounds)
      : (getBoundsByCenter(center, zoom.def) as LngLatBounds);
    const serviceName = service.name.replace(" ", "-");

    router.push(
      {
        pathname: "/veterinarian/[...area]",
        query: {
          _start: 0,
          geo_order: `${center.lat},${center.lng}`,
          ne: [bounds.ne.lng, bounds.ne.lat],
          sw: [bounds.sw.lng, bounds.sw.lat],
        },
      },
      {
        pathname: `/veterinarian/service/${serviceName}`,
        query: {
          _start: 0,
          geo_order: `${center.lat},${center.lng}`,
          ne: [bounds.ne.lng, bounds.ne.lat],
          sw: [bounds.sw.lng, bounds.sw.lat],
        },
      },
    );
  };

  const SliderCard = (service: IService) => (
    <SliderCardWrapper
      onClick={() => {
        amplitudeEvent(formatEventName(router.pathname, "services", service.name_id));
        redirectByService(service);
      }}
    >
      <ImageContainer>
        <NextImage src={service.icon} width={56} height={56} loader={({ src }) => src} />
      </ImageContainer>
      <Headline>{service.name}</Headline>
    </SliderCardWrapper>
  );

  const oneRowServices = [services[0], services[3], services[6], services[9]];
  const threeRowServices = services;

  return (
    <Wrapper>
      <Waves />
      <Content>
        <LargeTitle textColor={colors.white}>Most popular services</LargeTitle>
        <CardsWrapper>
          {size.width < sizesMedia.tablet && !open && (
            <Slider {...settings}>
              {oneRowServices.map((service) => (
                <SliderCard key={service.id} {...service} />
              ))}
            </Slider>
          )}

          {size.width < sizesMedia.tablet && open && (
            <Slider {...settings2}>
              {threeRowServices.map((service) => (
                <SliderCard key={service.id} {...service} />
              ))}
            </Slider>
          )}

          {size.width >= sizesMedia.tablet && !open && (
            <Slider {...settings}>
              {oneRowServices.map((service) => (
                <SliderCard key={service.id} {...service} />
              ))}
            </Slider>
          )}

          {size.width >= sizesMedia.tablet && open && (
            <Slider {...settings2}>
              {threeRowServices.map((service) => (
                <SliderCard key={service.id} {...service} />
              ))}
            </Slider>
          )}
        </CardsWrapper>

        <ButtonWrapper>
          <Button variant={EVariants.QUATERNARY} onClick={handleService}>
            {open ? "Hide services" : "View all services"}
          </Button>
        </ButtonWrapper>
      </Content>
    </Wrapper>
  );
};

export default ServiceSection;
